import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, Job, ProviderAccountSummary } from '../api/client'
import { useIsAdmin } from '../auth/AuthContext'
import { apiEndpointText, showsApiEndpoint, type LegApiEndpoint } from '../utils/apiEndpoint'
import {
  inboundMailboxText,
  outboundMailboxDetail,
  outboundMailboxText,
  resolveLegMailbox,
} from '../utils/legMailbox'
import { connectionCheckLabel, jobInboundLabel, jobOutboundLabel } from '../utils/methods'
import { legThresholdText, overallWindowText } from '../utils/sla'

export default function JobDetail() {
  const isAdmin = useIsAdmin()
  const { id } = useParams<{ id: string }>()
  const [job, setJob] = useState<Job | null>(null)
  const [testResult, setTestResult] = useState<React.ReactNode | null>(null)
  /**
   * The account records, for naming each leg's MAILBOX (W3). `null` means NOT READ.
   *
   * Seeded from the session snapshot so a second visit paints a name rather than "not
   * established", and revalidated in the same breath — `peekProviderAccounts` is explicitly not
   * a substitute for the fetch. A failed fetch leaves it null, which `legMailbox.ts` renders as
   * "the account list has not been read": that is a display outage and must never be shown as
   * "this leg has no mailbox", which is a configuration fact about the job.
   */
  const [accounts, setAccounts] = useState<ProviderAccountSummary[] | null>(
    api.peekProviderAccounts(),
  )

  useEffect(() => {
    if (id) api.getJob(id).then(setJob)
  }, [id])

  useEffect(() => {
    let live = true
    api.listProviderAccounts()
      .then(rows => { if (live) setAccounts(rows) })
      .catch(() => { /* stays null — see the state's own comment */ })
    return () => { live = false }
  }, [])

  const handleTestNow = async () => {
    if (!id) return
    const result = await api.testNow(id)
    setTestResult(`Run ${result.status}`)
    setTimeout(() => setTestResult(null), 10000)
  }

  const handleTestConnection = async () => {
    if (!id) return
    try {
      const result = await api.testConnection(id)
      const details = (
        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
            {result.success ? '✅ Connection OK' : '❌ Connection Failed'}
          </div>
          {result.results && Object.entries(result.results).map(([key, val]) => {
            const ok = val.status === 'success'
            return (
              <div key={key} style={{ fontSize: '0.8rem', padding: '0.25rem 0', borderBottom: '1px solid #333' }}>
                <strong>{connectionCheckLabel(key, job ? providerForCheckKey(job, key) : null)}:</strong>{' '}
                <span style={{ color: ok ? '#4caf50' : '#ef5350' }}>
                  {ok ? '✅' : '❌'} {val.message}
                </span>
              </div>
            )
          })}
        </div>
      )
      setTestResult(details)
      setTimeout(() => setTestResult(null), 15000)
    } catch (err: any) {
      setTestResult(`❌ Error: ${err.message || 'Request failed'}`)
      setTimeout(() => setTestResult(null), 10000)
    }
  }

  if (!job) return <div>Loading...</div>

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  /* W3 — WHICH MAILBOX EACH LEG USES. Resolved through `utils/legMailbox.ts` so the phrasing
     is one place and is executed by a test, rather than assembled from conditionals here.

     The inbound leg's mailbox IS its transport account when that account is a `mail` record —
     there is no second column for it — and the account's KIND is what decides, not the leg's
     method: a job whose inbound leg names an `efax_api` record reads no mailbox at all, and
     saying so is different from leaving a cell blank. */
  const outboundMailbox = resolveLegMailbox(job.outbound_mail_account_id, accounts)
  const inboundMailbox = resolveLegMailbox(job.inbound_account_id, accounts)
  const outboundMailboxNote = outboundMailboxDetail(outboundMailbox, inboundMailbox)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>{job.name}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isAdmin && (
            <>
              <button onClick={handleTestConnection} style={{ padding: '0.5rem 1rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '4px', cursor: 'pointer' }}>Test Connection</button>
              <button onClick={handleTestNow} style={{ padding: '0.5rem 1rem', background: '#FF4133', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Test Now</button>
              <Link to={`/jobs/${id}/edit`} style={{ padding: '0.5rem 1rem', border: '1px solid #FF4133', color: '#FF4133', borderRadius: '4px', textDecoration: 'none' }}>Edit</Link>
            </>
          )}
        </div>
      </div>

      {testResult && <div style={{ padding: '0.75rem', background: '#262626', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #333' }}>{testResult}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <Section title="Configuration">
          <Row label="Status" value={job.enabled ? '● Active' : '○ Disabled'} />
          {/* TWO labels, one per leg, each carrying its OWN provider. A mixed-provider
              job is supported (G9.8) so these are never collapsed into one row and the
              second is never hidden when they differ. */}
          <Row label="Outbound" value={jobOutboundLabel(job.outbound_provider, job.outbound_method)} />
          <Row label="Inbound" value={jobInboundLabel(job.inbound_provider, job.inbound_method)} />
          <Row label="Destination" value={job.destination_did} />
          {/* THE API ENDPOINT, PER LEG, FOR EVERY PROVIDER (W1-F).
              This row used to be `job.outbound_method === 'eapi' && job.api_base_url`, which
              is an eFax-only JOB column: an SFax or SRFax job showed NO endpoint while
              genuinely dialling one, and an eFax leg naming an `efax_api` account does not
              read that column either — `resolve_leg` treats a named account as
              authoritative — so the column could be present, populated by its model default,
              and NOT the host in use.

              The verdict is computed server-side (`_leg_api_endpoint`), including WHICH RECORD
              supplied it, and this only renders it. The value NEVER falls back to a brand's
              default: a leg whose endpoint could not be established says so, which is the
              `outboundHandle.ts` rule applied to a URL. A leg that dials no API at all — SMTP
              send, IMAP poll, a callback we receive — renders NOTHING, which is what the old
              method gate got right and what stops an SMTP job advertising an API it never
              calls.

              Both legs, because inbound-by-API is a real transport (`inbound_method='api'`,
              SRFax's inbox polling), so an endpoint is not an outbound-only property. */}
          {showsApiEndpoint(job.outbound_api_endpoint) && (
            <ApiEndpointRow leg="Outbound" endpoint={job.outbound_api_endpoint!} />
          )}
          {showsApiEndpoint(job.inbound_api_endpoint) && (
            <ApiEndpointRow leg="Inbound" endpoint={job.inbound_api_endpoint!} />
          )}
          <Row label="Schedule" value={job.cron_expression || `Every ${job.interval_minutes}min`} />
          {/* Per-leg thresholds are the only SLA controls; the job-wide one is retired.
              A blank leg is NOT EVALUATED — not a pass — and the overall window is the
              derived sum, stated so its removal as an input is not read as its removal
              as a check. */}
          <Row label="Outbound SLA" value={legThresholdText(job.outbound_sla_minutes)} />
          <Row label="Inbound SLA" value={legThresholdText(job.inbound_sla_minutes)} />
          <Row label="Overall window (derived)" value={overallWindowText(job)} />
          <Row label="Max In-Flight" value={String(job.max_in_flight)} />
        </Section>

        <Section title="Credentials & Document">
          {job.outbound_method === 'eapi' && <Row label="eAPI Credentials" value={job.has_api_credentials ? '✅ Configured' : '❌ Not set'} />}
          {job.outbound_method === 'smtp' && <>
            <Row label="SMTP Host" value={`${job.smtp_host}:${job.smtp_port}`} />
            <Row label="SMTP Credentials" value={job.has_smtp_credentials ? '✅ Configured' : '❌ Not set'} />
          </>}
          {/* NO IMAP CREDENTIAL ROW HERE, DELIBERATELY.
              There used to be one, reading the job's own encrypted IMAP username column. That
              column is only ONE of the two places a mailbox can live: `resolve_leg`
              (`backend/services/leg_credentials.py:1354`) treats a named account as
              AUTHORITATIVE, so an account-selected mailbox leaves the job column NULL and the
              row reported a fully configured mailbox as absent. Repairing the boolean was
              rejected: the `Mailboxes (per leg)` section below already answers this question
              correctly for BOTH homes, through `legMailbox.ts`, and a second weaker answer to a
              question already answered is duplication — duplication that DISAGREES is worse than
              absence. A per-leg credential indicator, if ever wanted, belongs inside that
              section computed from the same resolver, never as a separate row reading a
              different column. The flag itself stays on the API response: `JobEditor.tsx`'s
              `isStored` reads it through the registry's `presence_flag` to decide masking and
              required-ness, which is a question about the JOB's own stored secret and is the
              one question that column does answer. */}
          {job.inbound_method === 'eapi_webhook' && <Row label="eAPI Webhook HMAC" value={job.has_webhook_hmac_secret ? '✅ Signature verification enabled' : '⚠️ No secret — posts not verified'} />}

          {/* Document info */}
          <Row label="Document" value={job.document_original_name || 'None uploaded'} />
          {job.document_size_bytes && <Row label="Size" value={formatBytes(job.document_size_bytes)} />}
          {job.document_page_count && <Row label="Pages" value={String(job.document_page_count)} />}
          {job.document_original_name && (
            <div style={{ marginTop: '0.5rem' }}>
              <a
                href={`/api/jobs/${id}/document`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.8rem', color: '#FF4133', textDecoration: 'none' }}
              >
                ⬇ Download Document
              </a>
            </div>
          )}
        </Section>

        {/* W3 — MAILBOXES, PER LEG, AND ALWAYS BOTH ROWS.
            The outbound row is rendered unconditionally, including when the leg names no
            mailbox, because a blank there beside a populated inbound row reads as "the inbound
            mailbox is the outbound one too" — true for eFax and SFax, false for SRFax, and
            indistinguishable on screen. What unset MEANS is stated instead, and it depends on
            the inbound leg: an unset outbound mailbox means the confirmation is looked for in
            the inbound box, EXCEPT on a job with no inbound mailbox at all, where nothing
            collects it. That last permutation is the one nothing visibly breaks on — the job
            saves, sends, and simply never collects its notification — so it is the one this
            section exists to put on screen. */}
        <Section title="Mailboxes (per leg)">
          <Row label="Inbound faxes" value={inboundMailboxText(inboundMailbox)} />
          <Row label="Outbound confirmations" value={outboundMailboxText(outboundMailbox, inboundMailbox)} />
          {outboundMailboxNote && (
            <p style={{ fontSize: '0.72rem', color: '#9aa0a6', margin: '0.5rem 0 0' }}>
              {outboundMailboxNote}
            </p>
          )}
        </Section>

        {job.webhook_url && (
          <Section title="eAPI Webhook URL">
            <code style={{ wordBreak: 'break-all', fontSize: '0.85rem', background: '#262626', color: '#f5f5f5', padding: '0.5rem', display: 'block', borderRadius: '4px' }}>
              {job.webhook_url}
            </code>
            <p style={{ fontSize: '0.8rem', color: '#9aa0a6', marginTop: '0.5rem' }}>
              Registered per <strong>account</strong>, not per job — every job on this eFax API
              account receives on this same URL. Manage it, and its HMAC secret, under
              Settings &rarr; Provider Accounts.
            </p>
          </Section>
        )}
      </div>

      <div style={{ marginTop: '2rem' }}>
        <Link to={`/jobs/${id}/runs`} style={{ color: '#FF4133' }}>View Run History →</Link>
      </div>
    </div>
  )
}

/**
 * Which leg's provider a connection-check result key belongs to.
 *
 * The check result map is keyed by the leg it tested, and those keys are NOT the stored
 * method values — the eAPI check is still keyed 'api'. Routing each key to its own leg is
 * what makes a mixed-provider job label its two checks correctly instead of stamping one
 * provider across both.
 *
 * An unrecognised key (e.g. 'warning') gets NO provider, so `connectionCheckLabel` falls
 * back to its unqualified label rather than inventing a brand for a row that names none.
 */
const OUTBOUND_CHECK_KEYS = new Set(['api', 'eapi', 'smtp'])
const INBOUND_CHECK_KEYS = new Set(['webhook', 'eapi_webhook', 'imap'])

function providerForCheckKey(job: Job, key: string): string | null {
  const k = (key ?? '').trim().toLowerCase()
  if (OUTBOUND_CHECK_KEYS.has(k)) return job.outbound_provider ?? null
  if (INBOUND_CHECK_KEYS.has(k)) return job.inbound_provider ?? null
  return null
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#1e1e1e', borderRadius: '8px', padding: '1.2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', border: '1px solid #333' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#9aa0a6' }}>{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #2a2a2a' }}>
      <span style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>{label}</span>
      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

/**
 * One leg's API endpoint: the FQDN, the record it came from, and — when there is no FQDN —
 * the reason, ON THE PAGE.
 *
 * The reason is rendered as TEXT and not only as a `title` tooltip, because a tooltip is not a
 * surface anybody finds: the state this row exists to expose is a leg whose endpoint cannot be
 * established, and telling an operator that only on hover is barely better than the blank row
 * it replaces. It is shown for the gap states alone — a resolved endpoint needs no paragraph.
 */
function ApiEndpointRow({ leg, endpoint }: { leg: string; endpoint: LegApiEndpoint }) {
  return (
    <>
      <Row label={`${leg} API endpoint`} value={apiEndpointText(endpoint)} />
      {!endpoint.url && endpoint.detail && (
        <p style={{ fontSize: '0.72rem', color: '#9aa0a6', margin: '0.25rem 0 0.5rem' }}>
          {endpoint.detail}
        </p>
      )}
    </>
  )
}
